import React, { FC, useState } from 'react';
import { Field, Icon, TextArea, Toggle, Tooltip } from '@kontentino/ui';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import PostsConfig from 'app/config/posts';
import { UseSetStateAction } from 'utils/hooks/useSetState';
import { useTranslation } from 'react-i18next';

type Props = {
  onChange: UseSetStateAction<PostComposerFormState>;
  value: PostComposerFormState;
};

const PostComposerContentFirstComment: FC<Props> = ({ onChange, value }) => {
  const [isFirstCommentOpen, setIsFirstCommentOpen] = useState(
    !!value.attachments.firstComment?.text?.length,
  );
  const { t } = useTranslation();

  const handleFirstCommentValueChange = (comment: string) => {
    onChange((prev) => ({
      attachments: {
        ...prev.attachments,
        firstComment: {
          status: null,
          text: comment,
        },
      },
    }));
  };

  const handleToggleFirstCommentInput = () => {
    setIsFirstCommentOpen((prev) => {
      if (prev && !!value.attachments.firstComment?.text?.length) {
        const confirmResetFirstComment = window.confirm(
          t('firstComment.confirmResetFirstComment'),
        );

        if (!confirmResetFirstComment) return true;

        handleFirstCommentValueChange('');
      }

      return !prev;
    });
  };

  return (
    <Field.Group
      data-cy="post-composer-first-comment-field-group"
      data-name="posts-composer_post-content_first-comment-field-group"
    >
      <div className="tw-flex tw-items-center tw-justify-between">
        <Field.Label
          required
          data-cy="post-composer-first-comment-field-label"
          data-name="posts-composer_post-content_first-field-label"
        >
          {t('firstComment.addFirstCommentToPost')}
          <Tooltip
            content={
              <div className="tw-text-center">
                <div>{t('firstComment.firstCommentTooltip')}</div>
                {value.page.type === TYPE.INSTAGRAM && (
                  <span>{t('firstComment.instagramNote')}</span>
                )}
              </div>
            }
          >
            <Icon
              icon={faCircleInfo}
              size="sm"
              className="tw-ml-1 tw-text-grayscale-100"
            />
          </Tooltip>
        </Field.Label>
        <Toggle
          checked={isFirstCommentOpen}
          onChange={handleToggleFirstCommentInput}
          data-cy="post-composer-first-comment-toggle"
          data-name="posts-composer_post-content_first-comment-toggle"
        />
      </div>

      {isFirstCommentOpen && (
        <>
          <TextArea
            data-name="posts-composer_post-content_first-comment-textarea"
            data-cy="post-composer-first-comment-textarea"
            value={value.attachments.firstComment?.text || ''}
            onChange={(e) => {
              handleFirstCommentValueChange(e.target.value);
            }}
            maxLength={
              PostsConfig.FIRST_COMMENT_CHARACTER_LIMIT[value.page.type]
            }
          />
          <Field.Caption data-cy="post-composer-first-comment-caption">
            {`${value.attachments.firstComment?.text?.length || 0}/${
              PostsConfig.FIRST_COMMENT_CHARACTER_LIMIT[value.page.type]
            }`}
          </Field.Caption>
        </>
      )}
    </Field.Group>
  );
};

export default PostComposerContentFirstComment;
